import Avatar from 'ext-ui/components/Avatar/Avatar'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import ClockIcon from 'ext-ui/svg/icon/ClockIcon'
import CloseIcon from 'ext-ui/svg/icon/CloseIcon'
import React from 'react'
import { useSelector } from 'react-redux'
import BrandLogoIcon from 'src/components/BrandMarkLogo/BrandLogoIcon'
import RejectionNotice from 'src/feature/ManagerReview/RejectionNotice/RejectionNotice'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionDetailModel,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import { useQueryUser } from 'src/queries/users.queries'
import { selectAppName } from 'src/redux/selectors/session.selectors'

import style from './ManagerReviewSection.style'

interface ManagerReviewSectionProps {
  transaction: TransactionDetailModel
}

type ManagerReviewDisplayStatus =
  | 'pending'
  | 'reviewed'
  | 'auto-reviewed'
  | 'rejected'

export const getManagerReviewAssigneeId = (
  transaction: Pick<TransactionDetailModel, 'managerId' | 'managerReviewId'>,
): string | undefined => transaction.managerReviewId || transaction.managerId

const managerReviewDisplayStatus = (
  status?: TransactionManagerReviewStatus,
): ManagerReviewDisplayStatus => {
  if (status === TransactionManagerReviewStatus.Rejected) return 'rejected'
  if (status === TransactionManagerReviewStatus.AutoReviewed)
    return 'auto-reviewed'
  if (status === TransactionManagerReviewStatus.Reviewed) return 'reviewed'

  return 'pending'
}

const ManagerReviewSection: React.FC<ManagerReviewSectionProps> = ({
  transaction,
}): React.ReactElement | null => {
  const translate = useTranslate()
  const appName = useSelector(selectAppName)
  const managerId = getManagerReviewAssigneeId(transaction)
  const { data: manager } = useQueryUser({
    userId: managerId,
  })
  const displayStatus = managerReviewDisplayStatus(
    transaction.managerReviewStatus,
  )
  const isAutoReviewed =
    transaction.managerReviewStatus ===
    TransactionManagerReviewStatus.AutoReviewed

  if (!managerId && !isAutoReviewed) return null

  const statusIcon = {
    pending: (
      <ClockIcon
        width={16}
        height={16}
        aria-hidden={'true'}
        data-testid={'manager-review-status-icon'}
      />
    ),
    reviewed: (
      <CheckmarkIcon
        width={16}
        height={16}
        fill={'currentColor'}
        aria-hidden={'true'}
        data-testid={'manager-review-status-icon'}
      />
    ),
    'auto-reviewed': (
      <CheckmarkIcon
        width={16}
        height={16}
        fill={'currentColor'}
        aria-hidden={'true'}
        data-testid={'manager-review-status-icon'}
      />
    ),
    rejected: (
      <CloseIcon
        width={16}
        height={16}
        aria-hidden={'true'}
        data-testid={'manager-review-status-icon'}
      />
    ),
  }[displayStatus]

  const statusLabel = {
    pending: translate('Pending Review'),
    reviewed: translate('Reviewed'),
    'auto-reviewed': translate('Auto-reviewed'),
    rejected: translate('Rejected'),
  }[displayStatus]

  return (
    <div css={style} className={'managerReviewSection'}>
      <div className={'managerReviewSection-summary'}>
        <div className={'font-h3'}>{translate('Manager Review')}</div>
        <div className={'managerReviewSection-summary-details'}>
          <span
            className={`managerReviewSection-status managerReviewSection-status-${displayStatus}`}
          >
            {statusIcon}
            {statusLabel}
          </span>
          {isAutoReviewed ? (
            <span className={'managerReviewSection-manager'}>
              <BrandLogoIcon
                size={24}
                aria-hidden={'true'}
                data-testid={'manager-review-brand-logo'}
              />
              {appName}
            </span>
          ) : (
            manager && (
              <span className={'managerReviewSection-manager'}>
                <Avatar
                  data-testid={'manager-review-avatar'}
                  imageSrc={manager.avatarUrl}
                  fallback={
                    <span className={'managerReviewSection-avatar-initials'}>
                      {manager.initials}
                    </span>
                  }
                  size={24}
                  aria-hidden={'true'}
                />
                {manager.fullNameOrEmail}
              </span>
            )
          )}
        </div>
      </div>
      {transaction.managerReviewStatus ===
        TransactionManagerReviewStatus.Rejected && (
        <RejectionNotice transaction={transaction} />
      )}
    </div>
  )
}

export default ManagerReviewSection
